import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banknote, Check, Wallet } from 'lucide-react';
import { listAccounts } from '@/app/services/accountService';
import { getAccountIdsForSite } from '@/app/config/accountFilters';
import { useAuthStore } from '@/app/store/authStore';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import type { Account } from '@/app/types/account';

interface AccountPickerProps {
    open: boolean;
    onClose: () => void;
    onSelect: (account: Account) => void;
    selectedAccountId?: number | null;
}

// Only cash accounts are enabled for now. Bank accounts will be re-enabled
// later via a settings screen.
// Account ID reference:
//   1   = Cash - Mogappair
//   204 = Cash - Medavakkam
const CASH_ACCOUNT_IDS = new Set<number>([1, 204]);

export function AccountPicker({ open, onClose, onSelect, selectedAccountId }: AccountPickerProps) {
    const user = useAuthStore((state) => state.user);
    const siteId = user?.sites?.[0]?.id ?? null;
    const username = user?.username ?? null;

    const allowedIds = useMemo(
        () => new Set(getAccountIdsForSite(siteId, 'payment', username)),
        [siteId, username]
    );

    const { data: accounts = [], isLoading } = useQuery({
        queryKey: ['accounts-all'],
        queryFn: listAccounts,
        staleTime: 5 * 60_000,
        enabled: open,
    });

    const visibleAccounts = useMemo(() => {
        if (allowedIds.size === 0) return [];
        return accounts.filter(
            (a) => allowedIds.has(a.id) && a.is_active && CASH_ACCOUNT_IDS.has(a.id)
        );
    }, [accounts, allowedIds]);

    return (
        <MobileBottomSheet
            open={open}
            onClose={onClose}
            title="Received In"
            heightMode="auto"
        >
            <div className="flex flex-col max-h-[75vh]">
                {isLoading ? (
                    <ul className="divide-y divide-border">
                        {[1, 2, 3].map((i) => (
                            <li key={i} className="flex items-center gap-3 p-4 min-h-[64px]">
                                <MobileSkeleton className="w-11 h-11 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <MobileSkeleton className="h-4 w-28" />
                                    <MobileSkeleton className="h-3 w-16" />
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : allowedIds.size === 0 ? (
                    <MobileEmptyState
                        icon={<Wallet className="h-12 w-12" />}
                        title="No accounts configured"
                        description="No payment accounts are configured for your site. Contact your admin."
                    />
                ) : visibleAccounts.length === 0 ? (
                    <MobileEmptyState
                        icon={<Wallet className="h-12 w-12" />}
                        title="No accounts available"
                        description="The configured accounts are inactive or were not found."
                    />
                ) : (
                    <ul className="divide-y divide-border">
                        {visibleAccounts.map((account) => {
                            const isSelected = account.id === selectedAccountId;
                            return (
                                <li key={account.id}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onSelect(account);
                                            onClose();
                                        }}
                                        className="w-full flex items-center gap-3 p-4 min-h-[64px] text-left active:bg-accent transition-colors"
                                    >
                                        <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <Banknote className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-foreground truncate">
                                                {account.name}
                                            </p>
                                            <p className="text-xs text-muted-foreground">Cash</p>
                                        </div>
                                        {isSelected && (
                                            <Check className="h-5 w-5 text-primary flex-shrink-0" />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </MobileBottomSheet>
    );
}
